import { PaginatedDataDTO, PaginationDTO } from '@bottega52/commons-pagination';
import { Button, Collapse, Menu, MenuItem } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { Build, DeleteOutline, Edit, ExpandLess, ExpandMore, SearchOutlined } from '@material-ui/icons';
import SendIcon from '@material-ui/icons/Send';
import CheckBoxOutlinedIcon from '@material-ui/icons/CheckBoxOutlined';
import LastPageIcon from '@material-ui/icons/LastPage';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import { Stack } from '@mui/system';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import _ from 'lodash';
import * as React from "react";
import { IMKSRequestDTO } from '../../repository/jago/model/output/IMKSRequestDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { PERMISSIONS } from '../../services/PermissionService/PermissionConstants';
import { Roles } from '../../services/PermissionService/PermissionConstants';
import translations from '../../translations/i18next';
import ReduxLanguage from '../ReduxLanguage';
import moment from 'moment';
import { VAR_USERS_ROLES } from '../../utils/Utils';

const styles = createStyles({
  rowContainer: {
    display: 'flex',
    margin: 10,
    flexDirection: 'column'
  },
  rowBodyContainer: {
    padding: 15,
    minHeight: 100,
  },
  rowTitle: {
    fontWeight: 'bold'
  },
  logo: {
    width: '20%',
  },
  actionButtonText: {
    margin: 0,
    fontWeight: 'bold',
  },
  icon: {
    fontSize: 15,
    marginRight: 5,
  },
  actionButton: {
    width: 'fit-content',
    color: 'white',
    marginLeft: 20,
    padding: 4,
    paddingLeft: 10,
    paddingRight: 10,
    fontWeight: 'bold'
  },
  tableCellStyle: {
    padding: '12px',
    borderBottom: '1px solid #ddd',
  },
});

export const MKSRequestStatusOptions = {
  DRAFT: 'DRAFT',
  SENT: 'SENT',
  TAKEN_IN_CHARGE: 'TAKEN_IN_CHARGE',
  REJECTED: 'REJECTED',
  CANCELED: 'CANCELED',
  ORDER_CONFIRMED: 'ORDER_CONFIRMED',
  ORDER_SHIPPED: 'ORDER_SHIPPED',
  ORDER_COMPLETED: 'ORDER_COMPLETED',
};

export const MKSRequestTypeOptions = {
  KEY_DUPLICATION: 'KEY_DUPLICATION',
  CYLINDER_DUPLICATION: 'CYLINDER_DUPLICATION',
  KEY_EXTENSION: 'KEY_EXTENSION',
  CYLINDER_EXTENSION: 'CYLINDER_EXTENSION',
};

export const MKSImportFileFormatOptions = {
  LOCKBASE: 'LOCKBASE',
  SUPERLOCK: 'SUPERLOCK',
};

const getMKSRequestStatusColor = (status: string): string => {
  switch (status) {
    case MKSRequestStatusOptions.DRAFT:
      return '#999999';
    case MKSRequestStatusOptions.SENT:
      return '#FF9800';
    case MKSRequestStatusOptions.TAKEN_IN_CHARGE:
      return '#2196F3';
    case MKSRequestStatusOptions.REJECTED:
      return '#F44336';
    case MKSRequestStatusOptions.CANCELED:
      return '#9E9E9E';
    case MKSRequestStatusOptions.ORDER_CONFIRMED:
      return '#4CAF50';
    case MKSRequestStatusOptions.ORDER_SHIPPED:
      return '#00BCD4';
    case MKSRequestStatusOptions.ORDER_COMPLETED:
      return '#8BC34A';
    default:
      return '#000000';
  }
};

export interface IMKSRequestsTableState {
  anchorElActions: any;
  selectedRequest: IMKSRequestDTO | null;
  actionsStatusAdminOpen: boolean;
}

export interface IMKSRequestsTableProps extends WithStyles<typeof styles> {
  mksRequests: PaginatedDataDTO<IMKSRequestDTO>;
  rowsPerPage: number;
  onRowClick: (request: IMKSRequestDTO) => void;
  onFetchElementsOnPage: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onDeleteMKSRequestRequest: (request: IMKSRequestDTO) => void;
  onChangeStatusRequest: (request: IMKSRequestDTO, newStatus: string, deliveryDate?: number) => void;
  onEditDeliveryDate: (request: IMKSRequestDTO) => void;
  onRejectRequest: (request: IMKSRequestDTO) => void;
  tableColumns?: GridColumns<IMKSRequestDTO>;
  isLoading?: boolean;
}

class MKSRequestsTable extends React.Component<IMKSRequestsTableProps, IMKSRequestsTableState> {
  private defaultColumns: GridColumns<IMKSRequestDTO>;

  constructor(props: IMKSRequestsTableProps) {
    super(props);
    this.state = {
      selectedRequest: null,
      anchorElActions: null,
      actionsStatusAdminOpen: false
    }
  }

  public openActionsMenu(event, request) {
    this.setState({ selectedRequest: request });
    this.setState({ anchorElActions: event.currentTarget });
  }

  public closeActionsMenu() {
    this.setState({ selectedRequest: null });
    this.setState({ anchorElActions: null });
  };

  public toggleActionsStatusAdminOpen() {
    this.setState({ actionsStatusAdminOpen: !this.state.actionsStatusAdminOpen });
  }

  public onEditDeliveryDate(request: IMKSRequestDTO) {
    this.props.onEditDeliveryDate(request);
  }

  public onRejectRequest(request: IMKSRequestDTO) {
    this.props.onRejectRequest(request);
  }

  public render() {
    const { mksRequests, rowsPerPage, onRowClick, onPageSizeChange, onFetchElementsOnPage, classes, tableColumns, onChangeStatusRequest, onDeleteMKSRequestRequest, onEditDeliveryDate, onRejectRequest } = this.props;
    const { anchorElActions, selectedRequest, actionsStatusAdminOpen } = this.state;

    this.defaultColumns = [
      {
        field: 'id',
        headerName: 'Id',
        type: 'string',
        sortable: true,
        width: 40,
        renderCell: (params) => {
          const request = params.row;
          return (
            <div>
              <h3 style={{ margin: 0, fontSize: 13, whiteSpace: 'normal' }}>{request.id}</h3>
            </div>
          )
        }
      },
      {
        field: 'requestType',
        headerName: translations.t('mks.requestType'),
        type: 'string',
        width: 180,
        sortable: false,
        resizable: true,
        renderCell: (params: GridRenderCellParams) => {
          const requestType = params.row.requestType;
          return (
            <div>
              <h4 style={{ fontWeight: 'bold', fontSize: 14, whiteSpace: 'normal' }}>{translations.t(`mks.requestType_${requestType}`)}</h4>
            </div>
          )
        }
      },
      {
        field: 'plantHostname',
        headerName: translations.t('mks.plantHostname'),
        type: 'string',
        flex: 1,
        minWidth: 150,
        sortable: false,
        resizable: true,
        renderCell: (params) => {
          const hostname = params.value || '';
          const cleanHostname = hostname.replace("api-", "").replace(".jago.cloud", "");
          return (
            <div style={{ padding: '8px', fontSize: '14px' }}>
              {cleanHostname}
            </div>
          );
        }
      },
      {
        field: 'customerName',
        headerName: translations.t('mks.customerName'),
        type: 'string',
        flex: 1,
        minWidth: 150,
        sortable: false,
        resizable: true,
      },
      {
        field: 'varName',
        headerName: translations.t('mks.varName'),
        type: 'string',
        flex: 1,
        minWidth: 100,
        sortable: false,
        resizable: true,
      },
      {
        field: 'createdBy',
        headerName: translations.t('mks.createdBy'),
        type: 'string',
        flex: 1,
        minWidth: 150,
        sortable: false,
        resizable: true,
      },
      {
        field: 'status',
        headerName: translations.t('mks.requestStatus'),
        type: 'string',
        sortable: false,
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          const status = params.row.status;
          return (
            <div>
              <h5 style={{ color: getMKSRequestStatusColor(status), fontWeight: 'bold', whiteSpace: 'normal' }}>{translations.t(`mks.requestStatus_${status}`).toUpperCase()}</h5>
            </div>
          )
        }
      },
      {
        field: 'deliveryDate',
        headerName: translations.t('mks.deliveryDate'),
        type: 'string',
        sortable: false,
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          const deliveryDate = params.row.deliveryDate;
          return deliveryDate ? (
            <div>
              <h5 style={{ whiteSpace: 'normal' }}>{moment(deliveryDate).format('DD/MM/YYYY')}</h5>
            </div>
          ) : null;
        }
      },
    ];

    let finalTableColumns = tableColumns ? tableColumns : this.defaultColumns;

    const canCreateRequest = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.MKS_CREATE_REQUEST);
    const canManageRequest = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.MKS_MANAGE_REQUEST);

    finalTableColumns.push({
      field: 'actions',
      headerName: translations.t(' '),
      renderCell: (params: any) => {
        const request = params.row;
        const showActions = this.shouldShowActions(request, canCreateRequest, canManageRequest);
        return (
          <>
            {showActions ? (
              <td className={classes.tableCellStyle} style={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  style={{ backgroundColor: '#282c34', flexShrink: 0 }}
                  className={classes.actionButton}
                  onClick={(e) => { e.stopPropagation(); this.openActionsMenu(e, request) }}
                >
                  <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="forms.actions" /></h5>
                  {Boolean(anchorElActions) && selectedRequest && selectedRequest.id === request.id ?
                    <ExpandLess className={classes.icon} /> : <ExpandMore className={classes.icon} />
                  }
                </Button>
              </td>
            ) : null}
          </>
        )
      },
      type: 'string',
      sortable: false,
      minWidth: 150,
    });

    return (
      <div style={{ width: '100%' }}>
        <DataGrid
          getRowId={(d) => d.id}
          rows={mksRequests.content || []}
          columns={finalTableColumns}
          rowHeight={55}
          onPageSizeChange={(pageSize) => onPageSizeChange(pageSize)}
          onPageChange={page => onFetchElementsOnPage(page)}
          page={(mksRequests?.pagination as PaginationDTO)?.number}
          rowCount={(mksRequests?.pagination as PaginationDTO)?.totalElements}
          pageSize={rowsPerPage}
          onRowClick={row => onRowClick(row.row)}
          paginationMode="server"
          loading={this.props.isLoading}
          componentsProps={{
            pagination: {
              labelRowsPerPage: translations.t('forms.rowsPerPage')
            }
          }}
          components={{
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <SearchOutlined style={{ alignSelf: 'center' }} />
                  <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='mks.noRequestFindTitle' /></h3>
                  <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='mks.noRequestFindDescription' /></h4>
                </div>
              </Stack>
            ),
            NoResultsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}><ReduxLanguage languageKey='mks.noRequestFindTitle' /></h3>
                <h4 style={{ textAlign: 'center', marginTop: 0 }}><ReduxLanguage languageKey='mks.noRequestFindDescription' /></h4>
              </Stack>
            )
          }}
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
          }}
        />
        {selectedRequest ? (
          <Menu
            id="simple-menu"
            anchorEl={anchorElActions}
            keepMounted
            open={Boolean(anchorElActions)}
            onClose={(e) => { this.closeActionsMenu() }}
          >
            {this.renderStatusActions(selectedRequest, canCreateRequest, canManageRequest, onChangeStatusRequest, onDeleteMKSRequestRequest, onEditDeliveryDate, onRejectRequest)}
            {AbilityProvider.getAbilityHelper().hasRoles([Roles.ROLE_VASHO_SOFIA_ADMIN]) && (
              <>
                <MenuItem onClick={() => this.toggleActionsStatusAdminOpen()}>
                  <Build className={classes.icon} color="secondary" />
                  <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="activations.changeStatusAdmin" /></h5>
                  {actionsStatusAdminOpen ? <ExpandLess /> : <ExpandMore />}
                </MenuItem>
                <Collapse in={actionsStatusAdminOpen} timeout="auto" unmountOnExit>
                  {_.map(MKSRequestStatusOptions, status => (
                    <MenuItem key={status} onClick={() => { this.closeActionsMenu(); this.toggleActionsStatusAdminOpen(); onChangeStatusRequest(selectedRequest, status) }}>
                      <LastPageIcon className={classes.icon} style={{ color: 'black' }} />
                      <h5 className={classes.actionButtonText}><ReduxLanguage languageKey={`mks.requestStatus_${status}`} /></h5>
                    </MenuItem>
                  ))}
                </Collapse>
              </>
            )}
          </Menu>
        ) : null}
      </div>
    );
  }

  private shouldShowActions(request: IMKSRequestDTO, canCreateRequest: boolean, canManageRequest: boolean): boolean {
    const isVar = AbilityProvider.getAbilityHelper().hasRoles(VAR_USERS_ROLES.map(e=>{return e.name}))
    const isSofiaAdmin = AbilityProvider.getAbilityHelper().hasRoles([Roles.ROLE_VASHO_SOFIA_ADMIN]);
    
    // Check if user is VAR - only show actions for DRAFT status
    if (isVar) {
      return canCreateRequest && request.status === MKSRequestStatusOptions.DRAFT;
    }
    
    // Check if user is Sofia Admin - always show actions
    if (isSofiaAdmin) {
      return true;
    }
    
    // For other users, check based on permissions and status
    if (canManageRequest) {
      // Managers can see actions for most statuses except completed ones
      return ![MKSRequestStatusOptions.REJECTED, MKSRequestStatusOptions.CANCELED].includes(request.status as any);
    }
    
    if (canCreateRequest && (request.status === MKSRequestStatusOptions.DRAFT || request.status === MKSRequestStatusOptions.SENT)) {
      return true;
    }
    
    return false;
  }

  private renderStatusActions(
    request: IMKSRequestDTO,
    canCreateRequest: boolean,
    canManageRequest: boolean,
    onChangeStatusRequest: (request: IMKSRequestDTO, newStatus: string, deliveryDate?: number) => void,
    onDeleteMKSRequestRequest: (request: IMKSRequestDTO) => void,
    onEditDeliveryDate: (request: IMKSRequestDTO) => void,
    onRejectRequest: (request: IMKSRequestDTO) => void
  ) {
    const { classes } = this.props;
    const actions: JSX.Element[] = [];

    const isVar = AbilityProvider.getAbilityHelper().hasRoles(VAR_USERS_ROLES.map(e=>{return e.name}))

    // DRAFT -> SENT (only canCreateRequest)
    if (request.status === MKSRequestStatusOptions.DRAFT && canCreateRequest) {
      actions.push(
        <MenuItem key="send" onClick={(e) => { e.stopPropagation(); this.closeActionsMenu(); onChangeStatusRequest(request, MKSRequestStatusOptions.SENT) }}>
          <SendIcon className={classes.icon} color="primary" />
          <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="mks.sendRequest" /></h5>
        </MenuItem>
      );
    }

    // SENT -> TAKEN_IN_CHARGE (only canManageRequest and not VAR)
    if (request.status === MKSRequestStatusOptions.SENT && canManageRequest && !isVar) {
      actions.push(
        <MenuItem key="takeInCharge" onClick={(e) => { e.stopPropagation(); this.closeActionsMenu(); onChangeStatusRequest(request, MKSRequestStatusOptions.TAKEN_IN_CHARGE) }}>
          <CheckBoxOutlinedIcon className={classes.icon} color="primary" />
          <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="mks.takeInCharge" /></h5>
        </MenuItem>
      );
    }

    // TAKEN_IN_CHARGE -> REJECTED or ORDER_CONFIRMED (only canManageRequest and not VAR)
    if (request.status === MKSRequestStatusOptions.TAKEN_IN_CHARGE && canManageRequest && !isVar) {
      actions.push(
        <MenuItem key="reject" onClick={(e) => { e.stopPropagation(); this.closeActionsMenu(); onRejectRequest(request) }}>
          <ThumbDownIcon className={classes.icon} color="primary" />
          <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="mks.rejectRequest" /></h5>
        </MenuItem>
      );
      actions.push(
        <MenuItem key="confirm" onClick={(e) => { e.stopPropagation(); this.closeActionsMenu(); onChangeStatusRequest(request, MKSRequestStatusOptions.ORDER_CONFIRMED) }}>
          <CheckBoxOutlinedIcon className={classes.icon} color="primary" />
          <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="mks.confirmOrder" /></h5>
        </MenuItem>
      );
    }

    // ORDER_CONFIRMED -> ORDER_SHIPPED (only canManageRequest and not VAR, requires deliveryDate)
    if (request.status === MKSRequestStatusOptions.ORDER_CONFIRMED && canManageRequest && !isVar) {
      actions.push(
        <MenuItem key="ship" onClick={(e) => { e.stopPropagation(); this.closeActionsMenu(); onChangeStatusRequest(request, MKSRequestStatusOptions.ORDER_SHIPPED) }}>
          <LocalShippingIcon className={classes.icon} color="primary" />
          <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="mks.shipOrder" /></h5>
        </MenuItem>
      );
    }

    // ORDER_SHIPPED -> ORDER_COMPLETED (only canManageRequest and not VAR)
    if (request.status === MKSRequestStatusOptions.ORDER_SHIPPED && canManageRequest && !isVar) {
      actions.push(
        <MenuItem key="complete" onClick={(e) => { e.stopPropagation(); this.closeActionsMenu(); onChangeStatusRequest(request, MKSRequestStatusOptions.ORDER_COMPLETED) }}>
          <CheckBoxOutlinedIcon className={classes.icon} color="primary" />
          <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="mks.completeOrder" /></h5>
        </MenuItem>
      );
    }

    // Edit Delivery Date (only for ORDER_SHIPPED e ORDER_COMPLETED and canManageRequest and not VAR)
    if ((request.status === MKSRequestStatusOptions.ORDER_SHIPPED || request.status === MKSRequestStatusOptions.ORDER_COMPLETED) && canManageRequest && !isVar) {
      actions.push(
        <MenuItem key="editDeliveryDate" onClick={(e) => { e.stopPropagation(); this.closeActionsMenu(); this.onEditDeliveryDate(request) }}>
          <Edit className={classes.icon} color="primary" />
          <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="mks.editDeliveryDate" /></h5>
        </MenuItem>
      );
    }

    // DELETE action - Gestione permessi per eliminazione
    const isSofiaAdmin = AbilityProvider.getAbilityHelper().hasRoles([Roles.ROLE_VASHO_SOFIA_ADMIN]);
    let canDelete = false;
    
    if (isVar) {
      // VARs can only delete DRAFT requests
      canDelete = canCreateRequest && request.status === MKSRequestStatusOptions.DRAFT;
    } else {
      canDelete = 
        // SOFIA ADMIN può eliminare in ogni stato
        isSofiaAdmin ||
        // Chi ha solo permessi di creazione può eliminare DRAFT o SENT
        (canCreateRequest && [MKSRequestStatusOptions.DRAFT, MKSRequestStatusOptions.SENT].includes(request.status as any)) ||
        // Chi ha permessi di gestione può eliminare SENT o TAKEN_IN_CHARGE o ORDER_CONFIRMED
        (canManageRequest && [MKSRequestStatusOptions.SENT, MKSRequestStatusOptions.TAKEN_IN_CHARGE, MKSRequestStatusOptions.ORDER_CONFIRMED].includes(request.status as any));
    }

    if (canDelete) {
      actions.push(
        <MenuItem key="delete" onClick={(e) => { e.stopPropagation(); this.closeActionsMenu(); onDeleteMKSRequestRequest(request) }}>
          <DeleteOutline className={classes.icon} style={{ color: 'darkred' }} />
          <h5 className={classes.actionButtonText}><ReduxLanguage languageKey="forms.delete" /></h5>
        </MenuItem>
      );
    }

    return actions;
  }
}

export default withStyles(styles)(MKSRequestsTable);
